import { useCallback, useMemo } from 'react';

import { OPCOES_POR_PAGINA, POR_PAGINA_PADRAO, type PorPagina } from '@fiscoai/contracts';

import { useAtualizarUrl } from './url';

export type Ordem = 'asc' | 'desc';

export interface ConfigTabela<C extends string> {
  /** Nome da tabela (lembra a escolha de itens por página no navegador). */
  chave: string;
  campos: readonly C[];
  padrao: { ordenarPor: C; ordem: Ordem };
  /** Direção ao escolher uma coluna nova (texto costuma começar em A→Z). */
  ordemInicial?: Partial<Record<C, Ordem>>;
}

const chaveLocal = (chave: string) => `fiscoai:tabela:${chave}:porPagina`;

function porPaginaSalvo(chave: string): PorPagina | null {
  try {
    const n = Number(globalThis.localStorage.getItem(chaveLocal(chave)));
    return (OPCOES_POR_PAGINA as readonly number[]).includes(n) ? (n as PorPagina) : null;
  } catch {
    return null;
  }
}

function salvarPorPagina(chave: string, n: number): void {
  try {
    globalThis.localStorage.setItem(chaveLocal(chave), String(n));
  } catch {
    // Navegação privada/armazenamento bloqueado: segue sem lembrar.
  }
}

/** Ao mudar filtro ou busca, a lista volta à primeira página. */
export function voltarAPrimeiraPagina(proximo: URLSearchParams): void {
  proximo.delete('pagina');
}

function lerPorPagina(params: URLSearchParams, chave: string): PorPagina {
  const n = Number(params.get('porPagina'));
  if ((OPCOES_POR_PAGINA as readonly number[]).includes(n)) return n as PorPagina;
  return porPaginaSalvo(chave) ?? POR_PAGINA_PADRAO;
}

/**
 * Estado da tabela paginada na URL (`pagina`, `porPagina`, `ordenarPor`, `ordem`): o link guarda a
 * página e a ordem, e o voltar do navegador funciona. Valores inválidos caem no padrão.
 */
export function useEstadoTabela<C extends string>(config: ConfigTabela<C>) {
  const { params, atualizar } = useAtualizarUrl();
  const { chave, campos, padrao, ordemInicial } = config;

  const estado = useMemo(() => {
    const pagina = Math.max(1, Math.trunc(Number(params.get('pagina'))) || 1);
    const campo = params.get('ordenarPor');
    const ordenarPor = campos.includes(campo as C) ? (campo as C) : padrao.ordenarPor;
    const ordem: Ordem =
      params.get('ordem') === 'asc' || params.get('ordem') === 'desc'
        ? (params.get('ordem') as Ordem)
        : padrao.ordem;
    return { pagina, porPagina: lerPorPagina(params, chave), ordenarPor, ordem };
  }, [params, campos, padrao, chave]);

  const irParaPagina = useCallback(
    (pagina: number) => {
      atualizar((p) => {
        if (pagina <= 1) p.delete('pagina');
        else p.set('pagina', String(pagina));
      });
    },
    [atualizar],
  );

  const mudarPorPagina = useCallback(
    (porPagina: number) => {
      salvarPorPagina(chave, porPagina);
      atualizar((p) => {
        p.set('porPagina', String(porPagina));
        voltarAPrimeiraPagina(p);
      });
    },
    [atualizar, chave],
  );

  /** Mesma coluna inverte a direção; coluna nova começa na direção dela. */
  const ordenar = useCallback(
    (campo: string) => {
      if (!campos.includes(campo as C)) return;
      const mesma = estado.ordenarPor === campo;
      const ordem: Ordem = mesma
        ? estado.ordem === 'asc'
          ? 'desc'
          : 'asc'
        : (ordemInicial?.[campo as C] ?? 'desc');
      atualizar((p) => {
        p.set('ordenarPor', campo);
        p.set('ordem', ordem);
        voltarAPrimeiraPagina(p);
      });
    },
    [atualizar, campos, estado.ordenarPor, estado.ordem, ordemInicial],
  );

  return {
    ...estado,
    ordenacao: { campo: estado.ordenarPor as string, ordem: estado.ordem },
    irParaPagina,
    mudarPorPagina,
    ordenar,
  };
}
